import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import VideoCard from "@/components/VideoCard";
import LeadForm from "@/components/energy/LeadForm";
import { ToolShell, WhatsAppCTA } from "@/components/energy/tool";
import { Reveal } from "@/components/Reveal";
import { ASSETS, CFG_SPECS, CFG_APPLICATIONS } from "@/data/site";

export default function EnergyCfg() {
  const [leadMode, setLeadMode] = useState(null);

  return (
    <>
      <Seo title="BNM Carbon-Free Generator — Energy Intelligence" description="The BNM Carbon-Free Generator inside the Energy Intelligence platform: status, capacities, applications, demonstration video, and how to request information or quotation." />
      <ToolShell
        testid="energy-cfg"
        eyebrow="BNM Technology Option"
        title="BNM Carbon-Free Generator."
        lead="BNM's flagship clean-energy technology is part of the Energy Intelligence platform — a carbon-free generation and storage system being developed for homes, schools, clinics, farms and enterprises. 500W prototype built; 5kVA-class hybrid system on the roadmap."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <Reveal>
            <figure className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-lg">
              <img src={ASSETS.generator} alt="BNM Carbon Free Generator 500W prototype unit" className="w-full h-auto" width="1024" height="768" />
              <figcaption className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">
                Official BNM photo — 500W prototype
              </figcaption>
            </figure>
          </Reveal>
          <div className="space-y-6">
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-display text-lg font-bold text-slate-900">Status & Capacities</h2>
              <ul className="mt-4 space-y-2.5 text-sm text-slate-600">
                <li><b className="text-slate-900">Available today:</b> 500W prototype (development phase — not yet commercially deployed)</li>
                <li><b className="text-slate-900">Target:</b> up to 5kVA hybrid carbon-free generator technology</li>
                <li><b className="text-slate-900">Architecture:</b> carbon-free generation with integrated storage</li>
                <li className="text-slate-400 italic">Further specifications are under development and published when verified.</li>
              </ul>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="font-display text-lg font-bold text-slate-900">Intended Applications</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {CFG_APPLICATIONS.map((a) => (
                  <span key={a} className="rounded-full border border-slate-200 bg-mist px-3.5 py-1.5 text-xs font-medium text-slate-700">{a}</span>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-400">Potential application areas — no commercial deployments have been announced.</p>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">See it running</h2>
            <p className="mt-2 text-sm text-slate-600">Official footage from the BNM workshop in Waterloo, Sierra Leone.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button onClick={() => setLeadMode("info")} data-testid="cfg-request-info"
                className="rounded-md bg-obsidian px-6 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
                Request Information
              </button>
              <button onClick={() => setLeadMode("quote")} data-testid="cfg-request-quote"
                className="rounded-md border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-700 hover:border-emerald-400 transition-colors">
                Request Quotation
              </button>
              <Link to="/carbon-free-generator" data-testid="cfg-full-page-link"
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-700 hover:border-emerald-400 transition-colors">
                Full product page <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-4">
              <WhatsAppCTA message="Hello BNM. I'd like information about the Carbon-Free Generator." label="Ask about the generator on WhatsApp" />
            </div>
            <div className="mt-4">
              <button onClick={() => setLeadMode("partner")} data-testid="cfg-partner-link"
                className="text-sm font-semibold text-emerald-700 hover:underline">
                Partnership / investment inquiry →
              </button>
            </div>
          </div>
          <div className="rounded-lg bg-obsidian p-6">
            <VideoCard />
          </div>
        </div>

        {leadMode && (
          <div className="mt-8">
            <LeadForm
              type={leadMode === "partner" ? "assessment" : "quote"}
              title={leadMode === "info" ? "Request Information — Carbon-Free Generator" : leadMode === "quote" ? "Request Quotation — Carbon-Free Generator" : "Partnership / Investment Inquiry"}
              context={{ product: "BNM Carbon-Free Generator" }}
              defaultMessage="I'm interested in the BNM Carbon-Free Generator."
            />
          </div>
        )}
      </ToolShell>
    </>
  );
}
